import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import AdapterStatusTable from './AdapterStatusTable';
import {
  SHARED_TYPOGRAPHY,
  getProgressStyles,
  getProgressStylesByIndex,
} from '../../../theme';

const ModelAnalyticsAccordion = ({ modelUsageDistribution, adapters }) => {
  const hasData =
    modelUsageDistribution?.model_distribution &&
    modelUsageDistribution.model_distribution.length > 0;

  return (
    <Box sx={{ mt: 4 }}>
      <Accordion defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="model-analytics-content"
          id="model-analytics-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SettingsIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
            <Typography variant="h4" sx={SHARED_TYPOGRAPHY.h4Bold}>
              Model Configuration Analytics
            </Typography>
            <Typography
              variant="h6"
              sx={{ ...SHARED_TYPOGRAPHY.bodyMediumSecondary }}
            >
              ({modelUsageDistribution?.period || 'N/A'})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Adapter Status Section */}
          <AdapterStatusTable adapters={adapters} />

          <Grid container spacing={3}>
            {/* Model Usage Distribution */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={SHARED_TYPOGRAPHY.h5Bold}
                  >
                    Model Usage Distribution
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      {hasData ? (
                        modelUsageDistribution.model_distribution
                          .sort((a, b) => b.execution_count - a.execution_count)
                          .map((model, index) => (
                            <Box key={model.model_name} sx={{ mb: 2 }}>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={1}
                              >
                                <Typography
                                  variant="body1"
                                  sx={SHARED_TYPOGRAPHY.bodyBold}
                                >
                                  {model.model_name}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={SHARED_TYPOGRAPHY.bodySecondary}
                                >
                                  {model.execution_count} executions (
                                  {model.usage_percentage}%)
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={model.usage_percentage}
                                sx={getProgressStylesByIndex(index)}
                              />
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mt={0.5}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Success Rate: {model.success_rate}%
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Cost: ${model.total_cost.toFixed(4)}
                                </Typography>
                              </Box>
                            </Box>
                          ))
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: 'center', py: 4 }}
                        >
                          No model usage data available
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Provider Performance & Cost Analysis */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={SHARED_TYPOGRAPHY.h5Bold}
                  >
                    Provider Performance & Cost Analysis
                  </Typography>
                  <TableContainer sx={{ flex: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Model</TableCell>
                          <TableCell align="right">Success Rate</TableCell>
                          <TableCell align="right">
                            Avg Execution Time
                          </TableCell>
                          <TableCell align="right">Total Cost</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {hasData ? (
                          modelUsageDistribution.model_distribution
                            .sort(
                              (a, b) =>
                                (b.total_cost || 0) - (a.total_cost || 0)
                            )
                            .map(model => (
                              <TableRow key={model.model_name}>
                                <TableCell>
                                  <Typography
                                    variant="body1"
                                    sx={SHARED_TYPOGRAPHY.bodyBold}
                                  >
                                    {model.model_name}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    gap={1}
                                  >
                                    <LinearProgress
                                      variant="determinate"
                                      value={model.success_rate}
                                      sx={getProgressStyles(model.success_rate)}
                                    />
                                    <Typography
                                      variant="body1"
                                      sx={SHARED_TYPOGRAPHY.bodyBold}
                                    >
                                      {model.success_rate.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body1"
                                    sx={SHARED_TYPOGRAPHY.bodyMedium}
                                  >
                                    {model.avg_execution_time.toFixed(2)}s
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body1"
                                    sx={SHARED_TYPOGRAPHY.bodyBold}
                                  >
                                    ${(model.total_cost || 0).toFixed(4)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              align="center"
                              sx={{ py: 4 }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No model usage data available
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ModelAnalyticsAccordion;
